import { useState, useCallback } from 'react';
import { Application, Sequence, TreeNode, FileNode, FolderNode } from '../types';

export function useEctdParser() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parseApplications = useCallback(async (fileList: FileList): Promise<Application> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const files = Array.from(fileList);
      const appFolderName = detectAppFolder(files);
      const sequences = detectSequences(files, appFolderName);

      const seqData: Sequence[] = [];
      for (const seq of sequences) {
        const files = seq.files;
        const tree = await buildTreeWithXML(files, seq.id);
        const sequenceRegionalInfo = await extractRegionalInfo(files);
        seqData.push({ id: seq.id, tree, regionalInfo: sequenceRegionalInfo });
      }

      // Extract regional information from the first sequence
      const regionalInfo = await extractRegionalInfo(files);

      return { 
        id: appFolderName, 
        sequences: seqData,
        regionalInfo
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to parse eCTD application';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    parseApplications,
    isLoading,
    error,
  };
}

function detectAppFolder(files: File[]): string {
  if (!files || files.length === 0) {
    throw new Error('No files provided');
  }
  
  const firstFile = files[0];
  if (!firstFile || !(firstFile as any).webkitRelativePath) {
    console.error('First file:', firstFile);
    throw new Error('File missing webkitRelativePath property');
  }
  
  const pathParts = (firstFile as any).webkitRelativePath.split('/');
  return pathParts[0] || 'SharePoint-Application';
}

async function extractRegionalInfo(allFiles: File[]): Promise<{
  country: string;
  agency: string;
  dtdVersion: string;
  regionCode: string;
} | undefined> {
  try {
    // Look for regional XML in all files
    const regionalXml = allFiles.find(f => 
      (f.webkitRelativePath.includes('/au/') || f.webkitRelativePath.includes('/ca/') || 
       f.webkitRelativePath.includes('/us/') || f.webkitRelativePath.includes('/eu/') ||
       f.webkitRelativePath.includes('/fda/')) && 
      f.name.toLowerCase().includes('regional')
    );

    if (!regionalXml) return undefined;

    const xmlText = await regionalXml.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, 'text/xml');

    // Detect region based on XML structure
    let country = 'Unknown';
    let agency = 'Unknown';
    let regionCode = 'unknown';
    let dtdVersion = 'Unknown';

    const rootTag = doc.documentElement.tagName;
    const rootNamespace = doc.documentElement.namespaceURI;

    // Check for Canadian eCTD (hcsc_ectd)
    if (rootTag === 'hcsc_ectd' || rootNamespace === 'hcsc_ectd') {
      country = 'Canada';
      agency = 'Health Canada';
      regionCode = 'ca';
    }
    // Check for Australian eCTD (tga_ectd)
    else if (rootTag === 'tga_ectd' || rootNamespace === 'tga_ectd') {
      country = 'Australia';
      agency = 'TGA';
      regionCode = 'au';
    }
    // Check for US eCTD (fda-regional)
    else if (rootTag === 'fda-regional' || rootNamespace === 'http://www.ich.org/fda') {
      country = 'United States';
      agency = 'FDA';
      regionCode = 'us';
    }
    // Check for EU eCTD (eu:eu-backbone)
    else if (rootTag === 'eu-backbone' || rootNamespace === 'http://europa.eu.int') {
      country = 'European Union';
      agency = 'EMA';
      regionCode = 'eu';
    }

    // Try to extract DTD version (also called schema version)
    const dtdVersionAttr = doc.documentElement.getAttribute('dtd-version') ||
                          doc.documentElement.getAttribute('version') ||
                          doc.documentElement.getAttribute('schema-version');
    if (dtdVersionAttr) {
      dtdVersion = dtdVersionAttr;
    }

    return {
      country,
      agency,
      dtdVersion,
      regionCode
    };
  } catch (error) {
    console.warn('Failed to extract regional information:', error);
    return undefined;
  }
}

function detectSequences(files: File[], appFolderName: string) {
  const seqMap = new Map<string, File[]>();
  
  console.log(`[DEBUG] Detecting sequences from ${files.length} files`);
  console.log(`[DEBUG] App folder: ${appFolderName}`);
  
  for (const f of files) {
    const relativePath = (f as any).webkitRelativePath;
    if (!relativePath) {
      console.warn('[DEBUG] File missing webkitRelativePath:', f.name);
      continue;
    }
    
    const parts = relativePath.split('/');
    console.log(`[DEBUG] File path: ${relativePath}, parts: [${parts.join(', ')}]`);
    
    if (parts.length > 1) {
      const seqId = parts[1];
      if (!seqMap.has(seqId)) seqMap.set(seqId, []);
      seqMap.get(seqId)!.push(f);
      console.log(`[DEBUG] Added file to sequence ${seqId}`);
    } else {
      console.log(`[DEBUG] Skipping file with insufficient path parts: ${relativePath}`);
    }
  }
  
  const sequences = Array.from(seqMap.entries())
    .map(([id, seqFiles]) => ({ id, files: seqFiles }))
    .sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }));
    
  console.log(`[DEBUG] Detected sequences: [${sequences.map(s => s.id).join(', ')}]`);
  sequences.forEach(seq => {
    console.log(`[DEBUG] Sequence ${seq.id}: ${seq.files.length} files`);
  });
  
  return sequences;
}

async function buildTreeWithXML(files: File[], sequenceId: string): Promise<TreeNode[]> {
  // Find and parse XML files
  const xmlFiles = files.filter(f => f.name.toLowerCase().endsWith('.xml'));
  
  const regionalXml = xmlFiles.find(f => {
    const path = ((f as any).webkitRelativePath || '').toLowerCase();
    const hasRegionalFolder = ['/au/', '/ca/', '/eu/', '/us/', '/fda/'].some(region =>
      path.includes(region)
    );
    return hasRegionalFolder && f.name.toLowerCase().includes('regional');
  });
  const indexXml = xmlFiles.find(f => f.name.toLowerCase().includes('index.xml'));
  
  let xmlData: { regional?: Record<string, any>; index?: Record<string, any> } = {};
  
  if (regionalXml) {
    try {
      const regionalText = await regionalXml.text();
      xmlData.regional = parseRegionalXML(regionalText);
    } catch (e) {
      console.warn('Failed to parse regional XML:', e);
    }
  }
  
  if (indexXml) {
    try {
      const indexText = await indexXml.text();
      xmlData.index = parseIndexXML(indexText);
    } catch (e) {
      console.warn('Failed to parse index XML:', e);
    }
  }

  // Build the file tree
  const root: Record<string, any> = {};
  for (const f of files) {
    const relativePath = (f as any).webkitRelativePath;
    if (!relativePath) {
      console.warn('File missing webkitRelativePath:', f.name);
      continue;
    }
    
    const parts = relativePath.split('/').slice(1); // skip app folder
    let current = root;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (!current[part]) current[part] = (i === parts.length - 1) ? f : {};
      current = current[part];
    }
  }
  
  return orderNodes(objToNodesWithXML(root, sequenceId, xmlData));
}

function extractSequenceFromModifiedFile(modifiedFile: string | null | undefined): string | undefined {
  if (!modifiedFile) return undefined;
  const match = modifiedFile.match(/(\d{4})/);
  return match ? match[1] : undefined;
}

function parseRegionalXML(xmlText: string): Record<string, any> {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, 'text/xml');
  const result: Record<string, any> = {};
  
  // Check if this is Canadian XML (hcsc_ectd namespace)
  const isCanadian = doc.documentElement.tagName === 'hcsc_ectd' || 
                     doc.documentElement.getAttribute('xmlns') === 'hcsc_ectd' ||
                     doc.documentElement.namespaceURI === 'hcsc_ectd';
  
  // Check if this is Australian XML (tga_ectd namespace)
  const isAustralian = doc.documentElement.tagName === 'tga_ectd' || 
                       doc.documentElement.getAttribute('xmlns') === 'tga_ectd' ||
                       doc.documentElement.namespaceURI === 'tga_ectd';
  
  if (isCanadian) {
    // Parse Canadian XML structure
    const m1Container = doc.querySelector('m1-administrative-and-product-information');
    if (m1Container) {
      // Parse main folders
      const mainFolders = m1Container.children;
      for (const folder of mainFolders) {
        const folderName = folder.tagName;
        const folderTitle = getCanadianFolderTitle(folderName);
        
        // Store folder metadata
        result[folderName] = {
          type: 'folder',
          title: folderTitle,
          metadata: {}
        };
        
        // Parse subfolders and documents within this folder
        const subfolders = folder.children;
        for (const subfolder of subfolders) {
          const subfolderName = subfolder.tagName;
          const subfolderTitle = getCanadianSubfolderTitle(subfolderName);
          
          // Store subfolder metadata
          result[subfolderName] = {
            type: 'subfolder',
            title: subfolderTitle,
            parentFolder: folderName,
            metadata: {}
          };
          
          // Parse documents within this subfolder
          const leaves = subfolder.querySelectorAll('leaf');
          leaves.forEach(leaf => {
            const href = leaf.getAttribute('xlink:href');
            const operation = leaf.getAttribute('operation');
            const modifiedFile = leaf.getAttribute('modified-file');
            const title = leaf.querySelector('title')?.textContent?.trim();
            
            if (href && title) {
              const normalizedPath = href.replace(/\\/g, '/');
              const replacesSequence = operation === 'replace'
                ? extractSequenceFromModifiedFile(modifiedFile)
                : undefined;

              result[normalizedPath] = {
                title: title,
                operation: operation || 'new',
                type: 'leaf',
                folder: folderName,
                subfolder: subfolderName,
                modifiedFile,
                replacesSequence
              };
              
              const filename = href.split(/[/\\]/).pop();
              if (filename) {
                result[filename] = {
                  title: title,
                  operation: operation || 'new',
                  type: 'leaf',
                  folder: folderName,
                  subfolder: subfolderName,
                  modifiedFile,
                  replacesSequence
                };
              }
            }
          });
        }
      }
    }
  } else if (isAustralian) {
    // Parse Australian XML structure
    const m1Container = doc.querySelector('m1-au');
    if (m1Container) {
      // Parse main folders
      const mainFolders = m1Container.children;
      for (const folder of mainFolders) {
        const folderName = folder.tagName;
        const folderTitle = getAustralianFolderTitle(folderName);
        
        // Store folder metadata
        result[folderName] = {
          type: 'folder',
          title: folderTitle,
          metadata: {}
        };
        
        // Parse subfolders and documents within this folder
        const subfolders = folder.children;
        for (const subfolder of subfolders) {
          const subfolderName = subfolder.tagName;
          const subfolderTitle = getAustralianSubfolderTitle(subfolderName);
          
          // Store subfolder metadata
          result[subfolderName] = {
            type: 'subfolder',
            title: subfolderTitle,
            parentFolder: folderName,
            metadata: {}
          };
          
          // Parse documents within this subfolder
          const leaves = subfolder.querySelectorAll('leaf');
          leaves.forEach(leaf => {
            const href = leaf.getAttribute('xlink:href');
            const operation = leaf.getAttribute('operation');
            const modifiedFile = leaf.getAttribute('modified-file');
            const title = leaf.querySelector('title')?.textContent?.trim();
            
            if (href && title) {
              const normalizedPath = href.replace(/\\/g, '/');
              const replacesSequence = operation === 'replace'
                ? extractSequenceFromModifiedFile(modifiedFile)
                : undefined;

              result[normalizedPath] = {
                title: title,
                operation: operation || 'new',
                type: 'leaf',
                folder: folderName,
                subfolder: subfolderName,
                modifiedFile,
                replacesSequence
              };
              
              const filename = href.split(/[/\\]/).pop();
              if (filename) {
                result[filename] = {
                  title: title,
                  operation: operation || 'new',
                  type: 'leaf',
                  folder: folderName,
                  subfolder: subfolderName,
                  modifiedFile,
                  replacesSequence
                };
              }
            }
          });
        }
      }
    }
  } else {
    // Parse other XML structure (original logic)
    const leaves = doc.querySelectorAll('leaf');
    leaves.forEach(leaf => {
      const href = leaf.getAttribute('xlink:href');
      const operation = leaf.getAttribute('operation');
      const modifiedFile = leaf.getAttribute('modified-file');
      const title = leaf.querySelector('title')?.textContent?.trim();
      
      if (href && title) {
        const normalizedPath = href.replace(/\\/g, '/');
        const replacesSequence = operation === 'replace'
          ? extractSequenceFromModifiedFile(modifiedFile)
          : undefined;

        result[normalizedPath] = {
          title: title,
          operation: operation || 'new',
          type: 'leaf',
          modifiedFile,
          replacesSequence
        };
        
        const filename = href.split(/[/\\]/).pop();
        if (filename) {
          result[filename] = {
            title: title,
            operation: operation || 'new',
            type: 'leaf',
            modifiedFile,
            replacesSequence
          };
        }
      }
    });
  }
  
  return result;
}

function getCanadianFolderTitle(folderName: string): string {
  const folderTitles: Record<string, string> = {
    'm1-0-correspondence': '1.0 Correspondence',
    'm1-2-administrative-information': '1.2 Administrative Information',
    'm1-3-product-information': '1.3 Product Information'
  };
  
  return folderTitles[folderName] || folderName;
}

function getCanadianSubfolderTitle(subfolderName: string): string {
  const subfolderTitles: Record<string, string> = {
    'm1-0-1-cover-letter': '1.0.1 Cover Letter',
    'm1-0-2-life-cycle-management-table': '1.0.2 Life Cycle Management Table',
    'm1-0-3-copy-of-health-canada-issued-correspondence': '1.0.3 Health Canada Correspondence',
    'm1-2-1-application-forms': '1.2.1 Application Forms',
    'm1-2-2-fee-forms': '1.2.2 Fee Forms',
    'm1-2-3-certification-and-attestation-forms': '1.2.3 Certification and Attestation Forms',
    'm1-2-5-compliance-and-site-Information': '1.2.5 Compliance and Site Information',
    'm1-2-6-authorization-for-sharing-information': '1.2.6 Authorization for Sharing Information',
    'm1-3-6-certified-product-information-document': '1.3.6 Certified Product Information Document'
  };
  
  return subfolderTitles[subfolderName] || subfolderName;
}

function getAustralianFolderTitle(folderName: string): string {
  const folderTitles: Record<string, string> = {
    'm1-0-correspondence': '1.0 Correspondence',
    'm1-11-foreign': '1.11 Foreign'
  };
  
  return folderTitles[folderName] || folderName;
}

function getAustralianSubfolderTitle(subfolderName: string): string {
  const subfolderTitles: Record<string, string> = {
    'm1-0-1-cover': '1.0.1 Cover',
    'm1-0-2-tracking-table': '1.0.2 Tracking Table',
    'm1-0-3-response': '1.0.3 Response',
    'm1-11-4-eval-reports': '1.11.4 Evaluation Reports'
  };
  
  return subfolderTitles[subfolderName] || subfolderName;
}

function createIndexBasedFolderStructure(folderName: string, folderObj: any, sequenceId: string, xmlData: any): FolderNode | null {
  const moduleName = folderName.toLowerCase();
  const hierarchy = xmlData.index?.hierarchy?.[moduleName];
  
  if (!hierarchy) {
    return null;
  }
  
  const children = buildModuleTreeFromHierarchy(
    hierarchy,
    folderObj,
    sequenceId,
    moduleName
  );
  
  if (!children.length) {
    return null;
  }

  return {
    type: 'folder',
    name: folderName,
    children: children,
    expanded: false,
    metadata: {}
  } as FolderNode;
}

function buildModuleTreeFromHierarchy(
  structure: Record<string, any>,
  folderObj: any,
  sequenceId: string,
  moduleName: string
): TreeNode[] {
  const nodes: TreeNode[] = [];

  Object.entries(structure).forEach(([key, value]: [string, any]) => {
    if (!value) return;

    if (value.type === 'leaf') {
      const file =
        findFileByHref(folderObj, value.href, moduleName) ||
        findFileInFolder(folderObj, key);

      if (file) {
        nodes.push({
          type: 'file',
          name: value.title,
          originalName: key,
          file: file,
          sequenceId: sequenceId,
          lifecycleOp: value.operation || 'New',
          module: moduleName,
          xmlTitle: value.title,
          replacesSequence: value.replacesSequence,
          isStf: value.isStf,
          stfVersion: value.stfVersion,
          stfStudyId: value.stfStudyId
        } as FileNode);
      }
    } else {
      const childNodes = buildModuleTreeFromHierarchy(
        value.children || {},
        folderObj,
        sequenceId,
        moduleName
      );

      const displayName = value.xmlName || key;
      const xmlTitle = value.title || displayName;

      nodes.push({
        type: 'folder',
        name: displayName,
        children: childNodes,
        expanded: false,
        metadata: value.metadata || {},
        xmlTitle: xmlTitle
      } as FolderNode);
    }
  });

  return nodes;
}

function findFileInFolder(folderObj: any, filename: string): File | null {
  // Recursively search for a file in the folder object
  for (const [key, value] of Object.entries(folderObj)) {
    if (value instanceof File && key === filename) {
      return value;
    } else if (typeof value === 'object' && value !== null) {
      const found = findFileInFolder(value, filename);
      if (found) return found;
    }
  }
  return null;
}

function findFileByHref(folderObj: any, href: string | undefined, moduleName: string): File | null {
  if (!href) return null;

  const normalizedHref = href.replace(/\\/g, '/');
  const rawParts = normalizedHref.split('/').filter(Boolean);
  const parts: string[] = [];

  rawParts.forEach(part => {
    if (part === '..') {
      parts.pop();
    } else if (part !== '.') {
      parts.push(part);
    }
  });

  const moduleIndex = parts.findIndex(
    part => part.toLowerCase() === moduleName
  );
  const relevantParts =
    moduleIndex >= 0 ? parts.slice(moduleIndex + 1) : parts;

  let current = folderObj;
  for (let i = 0; i < relevantParts.length; i++) {
    const part = relevantParts[i];
    if (!current || typeof current !== 'object') {
      return null;
    }

    const value = (current as any)[part];

    if (i === relevantParts.length - 1) {
      return value instanceof File ? value : null;
    }

    current = value;
  }

  return null;
}

function extractStfMetadata(
  leaf: Element,
  normalizedHref: string
): {
  isStf: boolean;
  stfVersion?: string;
  stfStudyId?: string;
} {
  const versionAttr = leaf.getAttribute('version')?.trim() || undefined;
  const lowerHref = normalizedHref.toLowerCase();
  const fileSegment = normalizedHref.split('/').pop() || '';
  const lowerFileSegment = fileSegment.toLowerCase();

  const isLikelyStf =
    !!versionAttr?.toLowerCase().includes('stf') ||
    lowerFileSegment.startsWith('stf-') ||
    lowerHref.includes('/stf-') ||
    lowerHref.includes('/stf/');

  if (!isLikelyStf) {
    return { isStf: false };
  }

  const stfStudyId = deriveStudyIdFromHref(normalizedHref);

  return {
    isStf: true,
    stfVersion: versionAttr,
    stfStudyId,
  };
}

function deriveStudyIdFromHref(normalizedHref: string): string | undefined {
  const parts = normalizedHref.split('/').filter(Boolean);

  for (let i = parts.length - 2; i >= 0; i--) {
    const segment = parts[i];
    const lowerSegment = segment.toLowerCase();

    if (!segment) continue;
    if (lowerSegment.startsWith('m') && /^\d/.test(lowerSegment.slice(1))) continue;
    if (lowerSegment.startsWith('stf')) continue;
    if (lowerSegment.includes('stud-rep')) continue;
    if (lowerSegment.includes('study-rep')) continue;
    if (lowerSegment.startsWith('util')) continue;

    return normalizeStudyId(segment);
  }

  return undefined;
}

function normalizeStudyId(segment: string): string {
  return segment.replace(/_/g, '-').toUpperCase();
}

function parseIndexXML(xmlText: string): Record<string, any> {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, 'text/xml');
  const result: Record<string, any> = {};
  
  // Parse leaves for individual documents
  const leaves = doc.querySelectorAll('leaf');
  leaves.forEach(leaf => {
    const href = leaf.getAttribute('xlink:href');
    const operation = leaf.getAttribute('operation');
    const title = leaf.querySelector('title')?.textContent?.trim();
    
    if (href && title) {
      const normalizedPath = href.replace(/\\/g, '/');
      const modifiedFile = leaf.getAttribute('modified-file');
      let replacesSequence: string | undefined = undefined;
      const stfInfo = extractStfMetadata(leaf, normalizedPath);

      if (operation === 'replace' && modifiedFile) {
        const sequenceMatch = modifiedFile.match(/(\d{4})/);
        if (sequenceMatch) {
          replacesSequence = sequenceMatch[1];
        }
      }

      result[normalizedPath] = {
        title: title,
        operation: operation || 'new',
        type: 'leaf',
        href: normalizedPath,
        modifiedFile,
        replacesSequence,
        isStf: stfInfo.isStf,
        stfVersion: stfInfo.stfVersion,
        stfStudyId: stfInfo.stfStudyId
      };
      
      const filename = href.split(/[/\\]/).pop();
      if (filename) {
        result[filename] = {
          title: title,
          operation: operation || 'new',
          type: 'leaf',
          href: normalizedPath,
          modifiedFile,
          replacesSequence,
          isStf: stfInfo.isStf,
          stfVersion: stfInfo.stfVersion,
          stfStudyId: stfInfo.stfStudyId
        };
      }
    }
  });
  
  // Parse hierarchical structure from nodes
  const nodes = doc.querySelectorAll('node');
  nodes.forEach(node => {
    const metadata: Record<string, string> = {};
    
    Array.from(node.attributes).forEach(attr => {
      if (attr.name !== 'xmlns' && attr.name !== 'xlink:href') {
        metadata[attr.name] = attr.value;
      }
    });
    
    if (Object.keys(metadata).length > 0) {
      const nodePath = getNodePath(node);
      if (nodePath) {
        result[nodePath] = {
          type: 'node',
          metadata: metadata,
          children: []
        };
        
        const nodeName = getNodeName(node);
        if (nodeName) {
          result[nodeName] = {
            type: 'node',
            metadata: metadata,
            children: []
          };
        }
      }
    }
  });
  
  // Build hierarchical structure
  result.hierarchy = buildIndexHierarchy(doc);
  
  return result;
}

function buildIndexHierarchy(doc: Document): any {
  const hierarchy: any = {};
  
  // Find all module elements by tag name (m2-common-technical-document-summaries, etc.)
  const modules = doc.querySelectorAll('m2-common-technical-document-summaries, m3-quality, m4-nonclinical-study-reports, m5-clinical-study-reports');
  
  modules.forEach(module => {
    const tagName = module.tagName;
    
    // Extract module name from tag (m2, m3, m4, m5)
    const moduleName = tagName.split('-')[0];
    if (!hierarchy[moduleName]) {
      hierarchy[moduleName] = {};
    }
    
    // Parse the module structure
    const moduleStructure = parseModuleStructure(module);
    if (moduleStructure) {
      Object.assign(hierarchy[moduleName], moduleStructure);
    }
  });
  
  return hierarchy;
}

function parseModuleStructure(moduleElement: Element): any {
  const structure: any = {};
  
  // Get all child nodes and leaves
  const children = Array.from(moduleElement.children);
  
  children.forEach(child => {
    const tagName = child.tagName.toLowerCase();

    if (tagName === 'title') {
      return;
    }

    if (tagName === 'node') {
      const nodeId = child.getAttribute('id');
      const title = child.querySelector(':scope > title')?.textContent?.trim();
      
      if (nodeId) {
        // Extract metadata from attributes
        const metadata: Record<string, string> = {};
        Array.from(child.attributes).forEach(attr => {
          if (attr.name !== 'id' && attr.name !== 'xmlns' && attr.name !== 'xlink:href') {
            metadata[attr.name] = attr.value;
          }
        });
        
        // Parse nested structure
        const nestedStructure = parseModuleStructure(child);
        
        structure[nodeId] = {
          title: title,
          metadata: metadata,
          children: nestedStructure || {},
          xmlName: nodeId
        };
      }
    } else if (tagName === 'leaf') {
      // Handle direct leaf elements
      const href = child.getAttribute('xlink:href');
      const operation = child.getAttribute('operation');
      const title = child.querySelector(':scope > title')?.textContent?.trim();
      
      if (href && title) {
        const normalizedHref = href.replace(/\\/g, '/');
        const filename = href.split(/[/\\]/).pop();
        const modifiedFile = child.getAttribute('modified-file');
        let replacesSequence: string | undefined = undefined;
        const stfInfo = extractStfMetadata(child, normalizedHref);

        if (operation === 'replace' && modifiedFile) {
          const sequenceMatch = modifiedFile.match(/(\d{4})/);
          if (sequenceMatch) {
            replacesSequence = sequenceMatch[1];
          }
        }

        if (filename) {
          structure[filename] = {
            title: title,
            operation: operation || 'new',
            type: 'leaf',
            href: normalizedHref,
            modifiedFile,
            replacesSequence,
            isStf: stfInfo.isStf,
            stfVersion: stfInfo.stfVersion,
            stfStudyId: stfInfo.stfStudyId
          };
        }
      }
    } else {
      // Handle region-specific nested elements (e.g., m3-2-p-1-description..., node-extension, etc.)
      const metadata: Record<string, string> = {};
      Array.from(child.attributes).forEach(attr => {
        if (attr.name !== 'xmlns' && attr.name !== 'xlink:href') {
          metadata[attr.name] = attr.value;
        }
      });

      const nestedStructure = parseModuleStructure(child);

      const directTitleElement = Array.from(child.children).find(
        el => el.tagName.toLowerCase() === 'title'
      );
      const directTitle = directTitleElement?.textContent?.trim();

      let key = child.tagName;
      if (key === 'node-extension' && directTitle) {
        key = directTitle;
      }

      let uniqueKey = key;
      let counter = 1;
      while (structure[uniqueKey]) {
        uniqueKey = `${key} (${counter++})`;
      }

      structure[uniqueKey] = {
        title: directTitle,
        metadata: metadata,
        children: nestedStructure || {},
        xmlName: child.tagName
      };
    }
  });
  
  return Object.keys(structure).length > 0 ? structure : null;
}

function getNodeName(node: Element): string | null {
  const title = node.querySelector('title')?.textContent?.trim();
  if (title) return title;
  
  const textContent = node.textContent?.trim();
  if (textContent && textContent.length < 100) {
    return textContent;
  }
  
  return null;
}

function getNodePath(node: Element): string | null {
  const pathParts: string[] = [];
  let current: Element | null = node;
  
  while (current && current.nodeName !== 'ectd') {
    if (current.nodeName === 'node') {
      const textContent = current.textContent?.trim();
      if (textContent) {
        pathParts.unshift(textContent);
      } else {
        const title = current.querySelector('title')?.textContent?.trim();
        if (title) {
          pathParts.unshift(title);
        }
      }
    }
    current = current.parentElement;
  }
  
  return pathParts.join('/');
}

function objToNodesWithXML(obj: Record<string, any>, sequenceId: string, xmlData: any): TreeNode[] {
  return Object.entries(obj).map(([name, value]) => {
    if (value instanceof File) {
      const module = detectModule(value.webkitRelativePath);
      let xmlInfo = null;
      
      if (module === 'm1' && xmlData.regional) {
        xmlInfo = findXMLInfo(value.webkitRelativePath, xmlData.regional);
      } else if (['m2', 'm3', 'm4', 'm5'].includes(module) && xmlData.index) {
        xmlInfo = findXMLInfo(value.webkitRelativePath, xmlData.index);
      }
      
      return {
        type: 'file',
        name: xmlInfo?.title || name,
        originalName: name,
        file: value,
        sequenceId: sequenceId,
        lifecycleOp: xmlInfo?.operation || 'New',
        module: module,
        xmlTitle: xmlInfo?.title,
        replacesSequence: xmlInfo?.replacesSequence,
        isStf: xmlInfo?.isStf,
        stfVersion: xmlInfo?.stfVersion,
        stfStudyId: xmlInfo?.stfStudyId
      } as FileNode;
    } else {
      const folderMetadata = findFolderMetadata(name, xmlData);
      
      // For M1 folders, check if we need to create XML-based structure
      if (name.toLowerCase() === 'm1' && xmlData.regional) {
        if (hasCanadianXMLStructure(xmlData.regional)) {
          return createCanadianM1FolderStructure(name, value, sequenceId, xmlData);
        } else if (hasAustralianXMLStructure(xmlData.regional)) {
          return createAustralianM1FolderStructure(name, value, sequenceId, xmlData);
        }
      }
      
      // For M2-M5 folders, create hierarchical structure directly from index.xml when available
      if (['m2', 'm3', 'm4', 'm5'].includes(name.toLowerCase()) && xmlData.index?.hierarchy) {
        const xmlStructuredFolder = createIndexBasedFolderStructure(name, value, sequenceId, xmlData);
        if (xmlStructuredFolder) {
          return xmlStructuredFolder;
        }
      }
      
      // Apply Australian folder title formatting if this is an Australian submission
      let displayName = name;
      if (!xmlData.regional || !hasCanadianXMLStructure(xmlData.regional)) {
        // This is likely an Australian submission, apply Australian folder titles
        displayName = getAustralianFolderTitle(name) || getAustralianSubfolderTitle(name) || name;
      }
      
      return {
        type: 'folder',
        name: displayName,
        children: objToNodesWithXML(value, sequenceId, xmlData),
        expanded: false,
        metadata: folderMetadata
      } as FolderNode;
    }
  });
}

function hasCanadianXMLStructure(regionalData: Record<string, any>): boolean {
  return Object.values(regionalData).some((info: any) => info.type === 'folder' || info.type === 'subfolder');
}

function hasAustralianXMLStructure(regionalData: Record<string, any>): boolean {
  return Object.values(regionalData).some((info: any) => info.type === 'folder' || info.type === 'subfolder');
}

function createCanadianM1FolderStructure(folderName: string, folderObj: any, sequenceId: string, xmlData: any): FolderNode {
  const children: TreeNode[] = [];
  
  // Get all main folders from Canadian XML
  const mainFolders = Object.entries(xmlData.regional)
    .filter(([, info]: [string, any]) => info.type === 'folder')
    .map(([key, info]: [string, any]) => ({
      name: key,
      title: info.title,
      metadata: info.metadata || {}
    }));
  
  // Create main folders with their subfolders
  mainFolders.forEach(mainFolder => {
    const mainFolderChildren: TreeNode[] = [];
    
    // Get subfolders for this main folder
    const subfolders = Object.entries(xmlData.regional)
      .filter(([, info]: [string, any]) => info.type === 'subfolder' && info.parentFolder === mainFolder.name)
      .map(([key, info]: [string, any]) => ({
        name: key,
        title: info.title,
        metadata: info.metadata || {}
      }));
    
    
    // Create subfolders with their documents
    subfolders.forEach(subfolder => {
      const subfolderChildren: TreeNode[] = [];
      
      // Find files that belong to this subfolder
      // Need to look inside the 'ca' folder for Canadian submissions
      const caFolder = folderObj.ca || folderObj;
      const filesInSubfolder = Object.entries(caFolder)
        .filter(([, fileValue]) => {
          if (fileValue instanceof File) {
            const xmlInfo = findXMLInfo(fileValue.webkitRelativePath, xmlData.regional);
            return xmlInfo?.subfolder === subfolder.name;
          }
          return false;
        })
        .map(([fileName, fileValue]) => {
          const file = fileValue as File;
          const xmlInfo = findXMLInfo(file.webkitRelativePath, xmlData.regional);
          
          return {
            type: 'file' as const,
            name: xmlInfo?.title || fileName,
            originalName: fileName,
            file: file,
            sequenceId: sequenceId,
            lifecycleOp: xmlInfo?.operation || 'New',
            module: 'm1',
            xmlTitle: xmlInfo?.title,
            replacesSequence: xmlInfo?.replacesSequence
          } as FileNode;
        });
      
      subfolderChildren.push(...filesInSubfolder);
      
      if (subfolderChildren.length > 0) {
        mainFolderChildren.push({
          type: 'folder',
          name: subfolder.title,
          children: subfolderChildren,
          expanded: false,
          metadata: subfolder.metadata
        } as FolderNode);
      }
    });
    
    if (mainFolderChildren.length > 0) {
      children.push({
        type: 'folder',
        name: mainFolder.title,
        children: mainFolderChildren,
        expanded: false,
        metadata: mainFolder.metadata
      } as FolderNode);
    }
  });
  
  // Add any remaining files that don't belong to XML folders
  const caFolder = folderObj.ca || folderObj;
  const remainingFiles = Object.entries(caFolder)
    .filter(([, fileValue]) => {
      if (fileValue instanceof File) {
        const xmlInfo = findXMLInfo(fileValue.webkitRelativePath, xmlData.regional);
        return !xmlInfo?.folder; // Files not assigned to any XML folder
      }
      return false;
    })
    .map(([fileName, fileValue]) => {
      const file = fileValue as File;
      const xmlInfo = findXMLInfo(file.webkitRelativePath, xmlData.regional);
      
      return {
        type: 'file' as const,
        name: xmlInfo?.title || fileName,
        originalName: fileName,
        file: file,
        sequenceId: sequenceId,
        lifecycleOp: xmlInfo?.operation || 'New',
        module: 'm1',
        xmlTitle: xmlInfo?.title,
        replacesSequence: xmlInfo?.replacesSequence
      } as FileNode;
    });
  
  children.push(...remainingFiles);
  
  // Add any remaining subfolders (but exclude 'ca' since we've already processed it)
  const remainingSubfolders = Object.entries(folderObj)
    .filter(([subName, subValue]) => !(subValue instanceof File) && subName !== 'ca')
    .map(([subName, subValue]) => objToNodesWithXML({ [subName]: subValue }, sequenceId, xmlData)[0])
    .filter(Boolean);
  
  children.push(...remainingSubfolders);
  
  return {
    type: 'folder',
    name: folderName,
    children: children,
    expanded: false,
    metadata: findFolderMetadata(folderName, xmlData)
  } as FolderNode;
}

function createAustralianM1FolderStructure(folderName: string, folderObj: any, sequenceId: string, xmlData: any): FolderNode {
  const children: TreeNode[] = [];
  
  // Get all main folders from Australian XML
  const mainFolders = Object.entries(xmlData.regional)
    .filter(([, info]: [string, any]) => info.type === 'folder')
    .map(([key, info]: [string, any]) => ({
      name: key,
      title: info.title,
      metadata: info.metadata || {}
    }));
  
  // Create main folders with their subfolders
  mainFolders.forEach(mainFolder => {
    const mainFolderChildren: TreeNode[] = [];
    
    // Get subfolders for this main folder
    const subfolders = Object.entries(xmlData.regional)
      .filter(([, info]: [string, any]) => info.type === 'subfolder' && info.parentFolder === mainFolder.name)
      .map(([key, info]: [string, any]) => ({
        name: key,
        title: info.title,
        metadata: info.metadata || {}
      }));
    
    // Create subfolders with their documents
    subfolders.forEach(subfolder => {
      const subfolderChildren: TreeNode[] = [];
      
      // Find files that belong to this subfolder
      // Need to look inside the 'au' folder for Australian submissions
      const auFolder = folderObj.au || folderObj;
      const filesInSubfolder = Object.entries(auFolder)
        .filter(([, fileValue]) => {
          if (fileValue instanceof File) {
            const xmlInfo = findXMLInfo(fileValue.webkitRelativePath, xmlData.regional);
            return xmlInfo?.subfolder === subfolder.name;
          }
          return false;
        })
        .map(([fileName, fileValue]) => {
          const file = fileValue as File;
          const xmlInfo = findXMLInfo(file.webkitRelativePath, xmlData.regional);
          
          return {
            type: 'file' as const,
            name: xmlInfo?.title || fileName,
            originalName: fileName,
            file: file,
            sequenceId: sequenceId,
            lifecycleOp: xmlInfo?.operation || 'New',
            module: 'm1',
          xmlTitle: xmlInfo?.title,
          replacesSequence: xmlInfo?.replacesSequence
          } as FileNode;
        });
      
      subfolderChildren.push(...filesInSubfolder);
      
      if (subfolderChildren.length > 0) {
        mainFolderChildren.push({
          type: 'folder',
          name: subfolder.title,
          children: subfolderChildren,
          expanded: false,
          metadata: subfolder.metadata
        } as FolderNode);
      }
    });
    
    if (mainFolderChildren.length > 0) {
      children.push({
        type: 'folder',
        name: mainFolder.title,
        children: mainFolderChildren,
        expanded: false,
        metadata: mainFolder.metadata
      } as FolderNode);
    }
  });
  
  // Add any remaining files that don't belong to XML folders
  const auFolder = folderObj.au || folderObj;
  const remainingFiles = Object.entries(auFolder)
    .filter(([, fileValue]) => {
      if (fileValue instanceof File) {
        const xmlInfo = findXMLInfo(fileValue.webkitRelativePath, xmlData.regional);
        return !xmlInfo?.folder; // Files not assigned to any XML folder
      }
      return false;
    })
    .map(([fileName, fileValue]) => {
      const file = fileValue as File;
      const xmlInfo = findXMLInfo(file.webkitRelativePath, xmlData.regional);
      
      return {
        type: 'file' as const,
        name: xmlInfo?.title || fileName,
        originalName: fileName,
        file: file,
        sequenceId: sequenceId,
        lifecycleOp: xmlInfo?.operation || 'New',
        module: 'm1',
    xmlTitle: xmlInfo?.title,
    replacesSequence: xmlInfo?.replacesSequence
      } as FileNode;
    });
  
  children.push(...remainingFiles);
  
  // Add any remaining subfolders (but exclude 'au' since we've already processed it)
  const remainingSubfolders = Object.entries(folderObj)
    .filter(([subName, subValue]) => !(subValue instanceof File) && subName !== 'au')
    .map(([subName, subValue]) => objToNodesWithXML({ [subName]: subValue }, sequenceId, xmlData)[0])
    .filter(Boolean);
  
  children.push(...remainingSubfolders);
  
  return {
    type: 'folder',
    name: folderName,
    children: children,
    expanded: false,
    metadata: findFolderMetadata(folderName, xmlData)
  } as FolderNode;
}

function findFolderMetadata(folderName: string, xmlData: any): Record<string, string> | undefined {
  if (!xmlData.index) return undefined;
  
  for (const [xmlPath, rawInfo] of Object.entries(xmlData.index)) {
    const xmlInfo = rawInfo as { type?: string; metadata?: Record<string, string> };
    if (xmlInfo.type === 'node' && xmlInfo.metadata) {
      if (xmlPath.includes(folderName) || folderName.includes(xmlPath)) {
        return xmlInfo.metadata;
      }
      
      const moduleMatch = matchModuleSection(folderName, xmlPath);
      if (moduleMatch) {
        return xmlInfo.metadata;
      }
    }
  }
  
  return undefined;
}

function matchModuleSection(folderName: string, xmlPath: string): boolean {
  const folderParts = folderName.toLowerCase().split('-');
  const xmlParts = xmlPath.toLowerCase().split('/');
  
  for (let i = 0; i < folderParts.length; i++) {
    if (folderParts[i].startsWith('m') && folderParts[i].match(/^m\d+$/)) {
      const moduleNum = folderParts[i];
      if (xmlParts.some(part => part.includes(moduleNum))) {
        return true;
      }
    }
  }
  
  return false;
}

function detectModule(filepath: string): string {
  const pathParts = filepath.split('/');
  for (let i = 0; i < pathParts.length; i++) {
    const part = pathParts[i].toLowerCase();
    if (part.startsWith('m') && ['m1', 'm2', 'm3', 'm4', 'm5'].includes(part)) {
      return part;
    }
  }
  return 'unknown';
}

function findXMLInfo(filepath: string, xmlData: Record<string, any>): any {
  const normalizedPath = filepath.replace(/\\/g, '/');
  const filename = filepath.split(/[/\\]/).pop();
  
  if (xmlData[normalizedPath]) {
    return xmlData[normalizedPath];
  }
  
  if (filename && xmlData[filename]) {
    return xmlData[filename];
  }
  
  const pathParts = normalizedPath.split('/');
  const modulePart = pathParts.find(p => p.toLowerCase().startsWith('m'));
  
  if (modulePart) {
    for (const [xmlPath, xmlInfo] of Object.entries(xmlData)) {
      if (xmlPath.includes(modulePart) && xmlPath.includes(pathParts[pathParts.length - 1])) {
        return xmlInfo;
      }
    }
  }
  
  if (filename) {
    for (const [xmlPath, xmlInfo] of Object.entries(xmlData)) {
      if (xmlPath.includes(filename) || xmlPath.endsWith(filename)) {
        return xmlInfo;
      }
    }
  }
  
  return null;
}

function orderNodes(nodes: TreeNode[]): TreeNode[] {
  const folders = nodes.filter(n => n.type === 'folder') as FolderNode[];
  const files = nodes.filter(n => n.type === 'file') as FileNode[];

  folders.sort((a, b) => {
    if (a.name.toLowerCase() === 'm1') return -1;
    if (b.name.toLowerCase() === 'm1') return 1;
    return a.name.localeCompare(b.name, undefined, { numeric: true });
  });

  const nonXmlFiles = files
    .filter(f => !f.name.toLowerCase().endsWith('.xml'))
    .sort((a, b) => a.name.localeCompare(b.name));
  const xmlFiles = files
    .filter(f => f.name.toLowerCase().endsWith('.xml'))
    .sort((a, b) => a.name.localeCompare(b.name));

  return [
    ...folders.map(f => ({ ...f, children: orderNodes(f.children) })),
    ...nonXmlFiles,
    ...xmlFiles
  ];
}

